import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Filter, 
  Search, 
  Eye, 
  BarChart3,
  Users,
  Clock,
  Award,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAssessmentStore } from '../../stores/assessmentStore';
import StudentResultModal from './StudentResultModal';
import AssessmentAnalytics from './AssessmentAnalytics';

const AssessmentResults = ({ assessmentId }) => {
  const { currentAssessment, loadAssessment } = useAssessmentStore();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (assessmentId) {
      loadAssessment(assessmentId);
      loadSubmissions();
    }
  }, [assessmentId]);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchTerm, filterStatus]);

  const loadSubmissions = async () => {
    setIsLoading(true);
    try {
      // Mock data - replace with actual Firestore query
      const mockSubmissions = [
        {
          id: 'sub1',
          studentId: 'student1',
          studentName: 'John Doe',
          studentEmail: 'john@example.com',
          submittedAt: new Date('2024-01-15T10:30:00'),
          answers: { q1: 'A', q2: '2x + 3' },
          autoGradedScore: 15,
          manualGradedScore: 18,
          totalScore: 18,
          maxScore: 20,
          status: 'graded',
          timeSpent: 25 * 60, // 25 minutes in seconds
          attempt: 1
        },
        {
          id: 'sub2',
          studentId: 'student2',
          studentName: 'Jane Smith',
          studentEmail: 'jane@example.com',
          submittedAt: new Date('2024-01-15T11:45:00'),
          answers: { q1: 'B', q2: 'x^2 + 1' },
          autoGradedScore: 12,
          manualGradedScore: null,
          totalScore: null,
          maxScore: 20,
          status: 'pending',
          timeSpent: 32 * 60,
          attempt: 1
        }
      ];
      
      setSubmissions(mockSubmissions);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = submissions;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(sub => 
        sub.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.studentEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(sub => sub.status === filterStatus);
    }

    setFilteredSubmissions(filtered);
  };

  const getSubmissionStats = () => {
    const total = submissions.length;
    const graded = submissions.filter(s => s.status === 'graded').length;
    const pending = submissions.filter(s => s.status === 'pending').length;
    const avgScore = submissions
      .filter(s => s.totalScore !== null)
      .reduce((acc, s) => acc + (s.totalScore / s.maxScore * 100), 0) / Math.max(graded, 1);

    return { total, graded, pending, avgScore };
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (status, score, maxScore) => {
    if (status === 'pending') {
      return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
    
    const percentage = (score / maxScore) * 100;
    const passingScore = currentAssessment?.passingScore || 70;
    
    if (percentage >= passingScore) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    } else {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const stats = getSubmissionStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Assessment Results</h2>
          <p className="text-gray-600">{currentAssessment?.title}</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAnalytics(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </button>
          
          <button
            onClick={() => {/* Handle export */}}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Users className="w-5 h-5 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Submissions</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Graded</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.graded}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Pending</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <Award className="w-5 h-5 text-purple-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Average Score</p>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.avgScore.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
            >
              <option value="all">All Status</option>
              <option value="graded">Graded</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-500">
            Showing {filteredSubmissions.length} of {submissions.length} submissions
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubmissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {submission.studentName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {submission.studentEmail}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {submission.totalScore !== null ? (
                        <>
                          {submission.totalScore}/{submission.maxScore}
                          <span className="ml-2 text-gray-500">
                            ({((submission.totalScore / submission.maxScore) * 100).toFixed(1)}%)
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-400">Not graded</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(submission.status, submission.totalScore, submission.maxScore)}
                      <span className={clsx(
                        'ml-2 text-sm capitalize',
                        submission.status === 'graded' ? 'text-green-700' :
                        submission.status === 'pending' ? 'text-yellow-700' : 'text-gray-700'
                      )}>
                        {submission.status.replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-1" />
                      {formatDuration(submission.timeSpent)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {submission.submittedAt.toLocaleDateString()} {submission.submittedAt.toLocaleTimeString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedStudent(submission)}
                      className="text-sigma-600 hover:text-sigma-900 mr-3"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredSubmissions.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Submissions Found</h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your filters' 
                : 'No students have submitted this assessment yet'}
            </p>
          </div>
        )}
      </div>

      {/* Student Result Modal */}
      {selectedStudent && (
        <StudentResultModal
          submission={selectedStudent}
          assessment={currentAssessment}
          onClose={() => setSelectedStudent(null)}
          onGradeUpdate={(submissionId, newGrade) => {
            setSubmissions(prev => prev.map(sub =>
              sub.id === submissionId 
                ? { ...sub, manualGradedScore: newGrade, totalScore: newGrade, status: 'graded' }
                : sub
            ));
          }}
        />
      )}

      {/* Analytics Modal */}
      {showAnalytics && (
        <AssessmentAnalytics
          assessment={currentAssessment}
          submissions={submissions}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default AssessmentResults;